import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

const SEVERITY_STYLE = {
  critical: {
    pill: "border-rose-300 text-rose-700 bg-rose-50",
    border: "border-l-rose-500",
    icon: ShieldAlert,
    iconCls: "text-rose-600",
    label: "Kritisch",
  },
  warning: {
    pill: "border-amber-300 text-amber-700 bg-amber-50",
    border: "border-l-amber-500",
    icon: AlertTriangle,
    iconCls: "text-amber-600",
    label: "Beobachten",
  },
  ok: {
    pill: "border-emerald-300 text-emerald-700 bg-emerald-50",
    border: "border-l-emerald-500",
    icon: ShieldCheck,
    iconCls: "text-emerald-600",
    label: "OK",
  },
};

function selectCompany(companyId) {
  // Mirror logic of CompanyContext: persist + reload-less navigation hack
  try {
    localStorage.setItem("demoCompanyId", companyId);
  } catch (e) {
    // noop
  }
}

export default function GebuevAuditCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/advisor/gebuev-audit");
      setData(r.data);
    } catch (e) {
      // noop — Card bleibt leer
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data && !loading) return null;

  const visibleRows = expanded
    ? data?.companies || []
    : (data?.companies || []).slice(0, 5);

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="gebuev-audit-card">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-900">GeBüV-Audit Übersicht</h2>
          {data && (
            <>
              {data.critical_count > 0 && (
                <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50" data-testid="gebuev-audit-critical-pill">
                  {data.critical_count} kritisch
                </Badge>
              )}
              {data.warning_count > 0 && (
                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" data-testid="gebuev-audit-warning-pill">
                  {data.warning_count} beobachten
                </Badge>
              )}
              {data.ok_count > 0 && (
                <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="gebuev-audit-ok-pill">
                  {data.ok_count} OK
                </Badge>
              )}
            </>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="gebuev-audit-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {!data || data.companies.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400" data-testid="gebuev-audit-empty">
          Keine Mandanten gefunden.
        </div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="gebuev-audit-list">
            {visibleRows.map((c) => {
              const style = SEVERITY_STYLE[c.severity] || SEVERITY_STYLE.ok;
              const Icon = style.icon;
              return (
                <li
                  key={c.company_id}
                  className={`p-3 rounded-lg border-l-4 ${style.border} border-y border-r border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors`}
                  data-testid={`gebuev-audit-row-${c.company_id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={`h-4 w-4 ${style.iconCls} flex-shrink-0`} />
                      <span className="font-medium text-slate-900 truncate" data-testid={`gebuev-audit-name-${c.company_id}`}>
                        {c.company_name}
                      </span>
                      <Badge variant="outline" className={`text-[10px] ${style.pill}`}>
                        {style.label}
                      </Badge>
                    </div>
                    {c.severity !== "ok" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          selectCompany(c.company_id);
                          navigate("/compliance");
                        }}
                        data-testid={`gebuev-audit-cta-${c.company_id}`}
                      >
                        Periode festschreiben
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-500 grid grid-cols-1 sm:grid-cols-3 gap-1">
                    <div>
                      <span className="text-slate-400">Ältester Eintrag: </span>
                      <span className="font-medium text-slate-700">{c.oldest_unlocked_date || "—"}</span>
                      {c.days_unlocked > 0 && (
                        <span className="text-slate-400"> · {c.days_unlocked} Tage offen</span>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-400">Offene Buchungen: </span>
                      <span className="font-medium text-slate-700">{c.unlocked_entry_count}</span>
                    </div>
                    <div>
                      {c.last_lock_period_end ? (
                        <>
                          <span className="text-slate-400">Letzter Lock bis: </span>
                          <span className="font-medium text-slate-700">{c.last_lock_period_end}</span>
                        </>
                      ) : (
                        <span className="text-slate-400 italic">Noch nie festgeschrieben</span>
                      )}
                    </div>
                  </div>
                  {c.suggested_lock && (
                    <div className="mt-1 text-[11px] text-slate-500" data-testid={`gebuev-audit-suggest-${c.company_id}`}>
                      💡 Vorschlag: Periode <span className="font-mono font-medium">{c.suggested_lock}</span> festschreiben
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {(data.companies.length > 5) && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full text-xs"
              onClick={() => setExpanded((v) => !v)}
              data-testid="gebuev-audit-toggle-btn"
            >
              {expanded ? "Weniger anzeigen" : `Alle ${data.companies.length} Mandanten anzeigen`}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
